import {
  getFirestore,
  doc,
  getDoc,
  updateDoc,
  collection,
  query,
  where,
  getDocs,
  serverTimestamp,
  Timestamp
} from 'firebase/firestore'
import Database from '../types/Database'
import {
  PROVIDERS,
  isValidLevelForProvider,
  providerRequiresEmail
} from '../constants/accreditationLevels'

export type UserWithAccreditation = {
  id: string
  displayName: string
  email: string
  accreditation: Database.AccreditationObject
  guidedDiveFlag: boolean
}

// --- Validation helpers ---

const EMAIL_REGEX =
  /^[a-zA-Z0-9.!#$%&'*+/=?^_`{|}~-]+@[a-zA-Z0-9](?:[a-zA-Z0-9-]{0,61}[a-zA-Z0-9])?(?:\.[a-zA-Z0-9](?:[a-zA-Z0-9-]{0,61}[a-zA-Z0-9])?)*$/

function validateSubmissionData(
  data: Database.AccreditationSubmissionData,
  options?: { guidedDiveFlag?: boolean }
): void {
  const errors: string[] = []

  // type: must be a valid provider
  if (!PROVIDERS.includes(data.type as any)) {
    errors.push(`type must be one of: ${PROVIDERS.join(', ')}`)
  }

  // level: required and must be valid for provider
  if (!data.level || data.level.trim().length === 0) {
    errors.push('level is required')
  } else if (!isValidLevelForProvider(data.type, data.level)) {
    errors.push(
      `level "${data.level}" is not valid for provider "${data.type}"`
    )
  }

  // fullName: 1-100 chars
  if (
    !data.fullName ||
    data.fullName.length < 1 ||
    data.fullName.length > 100
  ) {
    errors.push('fullName must be between 1 and 100 characters')
  }

  // dateOfBirth: must be in the past, max 120 years ago
  if (!data.dateOfBirth || !(data.dateOfBirth instanceof Date)) {
    errors.push('dateOfBirth is required and must be a valid Date')
  } else {
    const now = new Date()
    if (data.dateOfBirth >= now) {
      errors.push('dateOfBirth must be in the past')
    }
    const maxAge = new Date()
    maxAge.setFullYear(maxAge.getFullYear() - 120)
    if (data.dateOfBirth < maxAge) {
      errors.push('dateOfBirth cannot be more than 120 years ago')
    }
  }

  // certificationNumber: 1-50 chars
  if (
    !data.certificationNumber ||
    data.certificationNumber.length < 1 ||
    data.certificationNumber.length > 50
  ) {
    errors.push('certificationNumber must be between 1 and 50 characters')
  }

  // optionalMemberId: 0-50 chars or null
  if (data.optionalMemberId !== null && data.optionalMemberId !== undefined) {
    if (data.optionalMemberId.length > 50) {
      errors.push('optionalMemberId must be 50 characters or fewer')
    }
  }

  // registeredEmail: required for providers that need it, RFC 5322 valid, max 254 chars
  if (providerRequiresEmail(data.type)) {
    if (!data.registeredEmail || data.registeredEmail.length === 0) {
      errors.push('registeredEmail is required for PADI accreditation')
    } else {
      if (data.registeredEmail.length > 254) {
        errors.push('registeredEmail must be 254 characters or fewer')
      }
      if (!EMAIL_REGEX.test(data.registeredEmail)) {
        errors.push('registeredEmail must be a valid email address')
      }
    }
  }

  // documentUrl: required
  if (!data.documentUrl || data.documentUrl.length === 0) {
    errors.push('documentUrl is required')
  } else if (data.documentUrl.length > 2048) {
    errors.push('documentUrl must be 2048 characters or fewer')
  }

  // Guided dive professional documents: required when opting in
  if (options?.guidedDiveFlag) {
    validateGuidedDiveDocs(data.guidedDiveDocuments, data.type, errors)
  }

  if (errors.length > 0) {
    throw new Error(`Validation failed: ${errors.join('; ')}`)
  }
}

function validateGuidedDiveDocs(
  docs: Database.GuidedDiveSubmissionDocs | undefined,
  provider: string,
  errors: string[]
): void {
  if (!docs) {
    errors.push('guidedDiveDocuments is required when opting in to guided dives')
    return
  }

  const now = new Date()

  if (!docs.professionalStatusUrl || docs.professionalStatusUrl.length === 0) {
    errors.push('Professional status document is required')
  }
  if (!docs.professionalStatusExpiry || !(docs.professionalStatusExpiry instanceof Date)) {
    errors.push('Professional status expiry date is required')
  } else if (docs.professionalStatusExpiry <= now) {
    errors.push('Professional status expiry date must be in the future')
  }

  if (!docs.insuranceUrl || docs.insuranceUrl.length === 0) {
    errors.push('Insurance document is required')
  }
  if (!docs.insuranceExpiry || !(docs.insuranceExpiry instanceof Date)) {
    errors.push('Insurance expiry date is required')
  } else if (docs.insuranceExpiry <= now) {
    errors.push('Insurance expiry date must be in the future')
  }

  // Affiliated center is only required for SSI and RAID (dive-center-based model)
  const requiresAffiliation = provider === 'ssi' || provider === 'raid'
  if (requiresAffiliation) {
    if (!docs.affiliatedCenter || docs.affiliatedCenter.trim().length === 0) {
      errors.push('Affiliated dive/training center is required for SSI/RAID')
    } else if (docs.affiliatedCenter.trim().length > 200) {
      errors.push('Affiliated center name must be 200 characters or fewer')
    }
  }

  if (!docs.localRegulationsAcknowledged) {
    errors.push(
      'You must acknowledge compliance with local maritime and commercial regulations'
    )
  }
}

function buildAccreditationObject(
  data: Database.AccreditationSubmissionData,
  options?: { guidedDiveFlag?: boolean }
): Record<string, unknown> {
  const base: Record<string, unknown> = {
    type: data.type,
    level: data.level,
    fullName: data.fullName,
    dateOfBirth: Timestamp.fromDate(data.dateOfBirth),
    certificationNumber: data.certificationNumber,
    optionalMemberId: data.optionalMemberId ?? null,
    documentUrl: data.documentUrl,
    status: 'pending',
    submittedAt: serverTimestamp(),
    reviewedAt: null,
    reviewedBy: null
  }

  if (providerRequiresEmail(data.type)) {
    base.registeredEmail = data.registeredEmail
  }

  if (options?.guidedDiveFlag && data.guidedDiveDocuments) {
    base.guidedDiveDocuments = {
      professionalStatusUrl: data.guidedDiveDocuments.professionalStatusUrl,
      professionalStatusExpiry: Timestamp.fromDate(
        data.guidedDiveDocuments.professionalStatusExpiry
      ),
      insuranceUrl: data.guidedDiveDocuments.insuranceUrl,
      insuranceExpiry: Timestamp.fromDate(
        data.guidedDiveDocuments.insuranceExpiry
      ),
      affiliatedCenter: data.guidedDiveDocuments.affiliatedCenter?.trim() || null,
      localRegulationsAcknowledged:
        data.guidedDiveDocuments.localRegulationsAcknowledged
    }
  }

  return base
}

// --- Public service functions ---

export async function submitAccreditation(
  userId: string,
  data: Database.AccreditationSubmissionData,
  options?: { guidedDiveFlag?: boolean }
): Promise<void> {
  validateSubmissionData(data, options)

  const db = getFirestore()
  const userRef = doc(db, 'users', userId)
  const userSnap = await getDoc(userRef)

  if (!userSnap.exists()) {
    throw new Error('User document not found')
  }

  const userData = userSnap.data()
  const existing = userData.accreditation

  // Reject if accreditation exists and status is not "rejected"
  if (existing !== null && existing !== undefined) {
    throw new Error(
      'An accreditation record already exists. Use resubmitAccreditation if status is rejected.'
    )
  }

  const accreditationObject = buildAccreditationObject(data, options)

  // eslint-disable-next-line @typescript-eslint/no-explicit-any
  const updatePayload: Record<string, any> = {
    accreditation: accreditationObject,
    updatedAt: serverTimestamp()
  }

  if (options?.guidedDiveFlag === true) {
    updatePayload.guidedDiveFlag = true
  }

  await updateDoc(userRef, updatePayload)
}

export async function resubmitAccreditation(
  userId: string,
  data: Database.AccreditationSubmissionData,
  options?: { guidedDiveFlag?: boolean }
): Promise<void> {
  const db = getFirestore()
  const userRef = doc(db, 'users', userId)
  const userSnap = await getDoc(userRef)

  if (!userSnap.exists()) {
    throw new Error('User document not found')
  }

  const userData = userSnap.data()
  const existing = userData.accreditation

  if (!existing || existing.status !== 'rejected') {
    throw new Error(
      'Resubmission is only allowed when the current accreditation status is "rejected"'
    )
  }

  validateSubmissionData(data, options)

  const accreditationObject = buildAccreditationObject(data, options)

  // eslint-disable-next-line @typescript-eslint/no-explicit-any
  const updatePayload: Record<string, any> = {
    accreditation: accreditationObject,
    updatedAt: serverTimestamp()
  }

  if (options?.guidedDiveFlag === true) {
    updatePayload.guidedDiveFlag = true
  }

  await updateDoc(userRef, updatePayload)
}

export async function updateAccreditationStatus(
  userId: string,
  status: 'verified' | 'rejected',
  adminUid: string
): Promise<void> {
  const db = getFirestore()

  // Validate caller is admin
  const adminRef = doc(db, 'users', adminUid)
  const adminSnap = await getDoc(adminRef)

  if (!adminSnap.exists()) {
    throw new Error('Admin user document not found')
  }

  const adminData = adminSnap.data()
  if (adminData.role !== 'admin') {
    throw new Error('Insufficient permissions: caller is not an admin')
  }

  // Validate target user's current accreditation status
  const userRef = doc(db, 'users', userId)
  const userSnap = await getDoc(userRef)

  if (!userSnap.exists()) {
    throw new Error('User document not found')
  }

  const userData = userSnap.data()
  const existing = userData.accreditation

  if (!existing) {
    throw new Error('No accreditation record exists for this user')
  }

  if (existing.status !== 'pending') {
    throw new Error(
      'Status update is only allowed when the current status is "pending". This submission has already been reviewed.'
    )
  }

  // eslint-disable-next-line @typescript-eslint/no-explicit-any
  const updatePayload: Record<string, any> = {
    'accreditation.status': status,
    'accreditation.reviewedAt': serverTimestamp(),
    'accreditation.reviewedBy': adminUid,
    updatedAt: serverTimestamp()
  }

  if (status === 'rejected') {
    updatePayload.guidedDiveFlag = false
  }

  await updateDoc(userRef, updatePayload)
}

export async function getPendingSubmissions(): Promise<
  UserWithAccreditation[]
> {
  const db = getFirestore()
  const usersRef = collection(db, 'users')
  const q = query(usersRef, where('accreditation.status', '==', 'pending'))
  const snapshot = await getDocs(q)

  const results: UserWithAccreditation[] = []

  snapshot.forEach(docSnap => {
    const data = docSnap.data()
    results.push({
      id: docSnap.id,
      displayName: data.displayName,
      email: data.email,
      accreditation: data.accreditation as Database.AccreditationObject,
      guidedDiveFlag: data.guidedDiveFlag === true
    })
  })

  return results
}

/** Re-qualification: update expired professional docs to regain guided dive eligibility. */
export async function requalifyGuidedDive(
  userId: string,
  docs: Database.GuidedDiveSubmissionDocs
): Promise<void> {
  const db = getFirestore()
  const userRef = doc(db, 'users', userId)
  const userSnap = await getDoc(userRef)

  if (!userSnap.exists()) {
    throw new Error('User document not found')
  }

  const userData = userSnap.data()

  if (!userData.accreditation || userData.accreditation.status !== 'verified') {
    throw new Error(
      'Re-qualification is only available for users with a verified accreditation'
    )
  }

  const provider = userData.accreditation.type as string
  const errors: string[] = []
  validateGuidedDiveDocs(docs, provider, errors)
  if (errors.length > 0) {
    throw new Error(`Validation failed: ${errors.join('; ')}`)
  }

  await updateDoc(userRef, {
    'accreditation.guidedDiveDocuments': {
      professionalStatusUrl: docs.professionalStatusUrl,
      professionalStatusExpiry: Timestamp.fromDate(
        docs.professionalStatusExpiry
      ),
      insuranceUrl: docs.insuranceUrl,
      insuranceExpiry: Timestamp.fromDate(docs.insuranceExpiry),
      affiliatedCenter: docs.affiliatedCenter?.trim() || null,
      localRegulationsAcknowledged: docs.localRegulationsAcknowledged
    },
    guidedDiveFlag: true,
    updatedAt: serverTimestamp()
  })
}
